import React, { useState, useEffect } from 'react';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { cloneDeep, get, isEmpty, isEqual } from 'lodash';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import { Splitter } from '@progress/kendo-react-layout';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import { isValidPhoneNumber } from 'libphonenumber-js';

import BobModal from '../../../components/BobModal';
import BobConfirmButton from './../../../components/BobConfirmButton';
import BobLoadingMask from '../../../components/BobLoadingMask';
import GoogleLogo from '../../../components/GoogleLogo';
import UserTabsContainer from './UserTabsContainer';
import AutoComplete from '@material-ui/lab/Autocomplete';
import { TimezoneOffsets } from '../../../helpers/timezones';
import BobContactPhoneInput from '../../../components/BobContactPhoneInput';
import useWindowSize from '../../../hooks/useWindowSize';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as RoleActions from '../../../actions/roleActions';
import * as ServiceManagementActions from '../../../actions/serviceManagementActions';
import * as SvcRequestActions from '../../../actions/svcRequestActions';

import { connect } from 'react-redux';

import classes from '../CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    firstName:      Yup.string().required(),
    lastName:       Yup.string().required(),
    email:          Yup.string().required(),
    addressSearch:  Yup.string().required(),
    timeZone:       Yup.string().required('This field is required.').nullable(),
    dateFormat:     Yup.string().required(),
    phone:          Yup.string().required(),
    timeFormat:     Yup.string().required(),
    defaultCompany: Yup.string(),
    isSuperUser:    Yup.boolean()
});

const DEFAULT_FORM_VALUES = {
    firstName:      '',
    lastName:       '',
    email:          '',
    addressSearch:  '',
    timeZone:       null,
    dateFormat:     '',
    phone:          '',
    defaultCompany: '',
    timeFormat:     '',
    isSuperUser:    false,
};

const DATE_FORMATS = [
    {
        id:   'MM/DD/YY',
        name: 'MM/DD/YY'
    },
    {
        id:   'MM/DD/YYYY',
        name: 'MM/DD/YYYY'
    },
    {
        id:   'DD/MM/YY',
        name: 'DD/MM/YY'
    },
    {
        id:   'DD/MM/YYYY',
        name: 'DD/MM/YYYY'
    },
    {
        id:   'YY/MM/DD',
        name: 'YY/MM/DD'
    },
    {
        id:   'YYYY/MM/DD',
        name: 'YYYY/MM/DD'
    }
];

const TIME_FORMATS = [
    {
        id:   'HH:mm',
        name: '24 Hour format 18:45 (HH:mm)'
    },
    {
        id:   'H:mm A',
        name: '12 Hour format 6:45 AM/PM (H:mm A)'
    }
];

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const UserDetails = ({
    addUserType,
    applications,
    bobLoading,
    companies,
    companyInfo,
    createUser,
    createUserCompanyApplication,
    currentUser,
    fetchAllScopes,
    fetchUserApplications,
    fetchUserContactDetails,
    isCreateUser,
    onCancelClick,
    selectedUser,
    updateUser,
    updateUserCompanyApplication,
    ...props
}) => {
    const [address, setAddress] = useState({});
    const [address1, setAddress1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [initialValues, setInitialValues] = useState({...DEFAULT_FORM_VALUES});
    const [selectedUserTypeList, setSelectedUserTypeList] = useState([]);
    const [createUserApplication, setCreateUserApplication] = useState(true);
    const [userApplicationId, setUserApplicationId] = useState('');
    const [userApplications, setUserApplications] = useState([]);
    const [userApplicationsCopy, setUserApplicationsCopy] = useState([]);
    const [isnWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [userContactDetails, setUserContactDetails] = useState([]);

    const [userTabIndex, setUserTabIndex] = useState(0);
    const [contextState, setContextState] = useState({});
    const [splitterHeight, setSplitterHeight] = useState('72rem');
    const [viewSuperUserCheckbox, setViewSuperUserCheckbox] = useState(false);

    const windowSize = useWindowSize();


    useEffect(() => {
        if (windowSize.width >= 2560) {
            setSplitterHeight('107rem');
        }
        else if (window.devicePixelRatio > 1) {
            setSplitterHeight('50rem');
        }
        else {
            setSplitterHeight('72rem');
        }
    }, [windowSize]);
    
    useEffect(() => {
        // when user only has access to one company then default to that company on save time
        if (isCreateUser && companies && companies.length === 1) {
            formik.setFieldValue('defaultCompany', companies.company.id);
        }
    },[companies]);

    const [horizontalPanes, setHorizontalPanes] = useState([{
        min:         '20px',
        size:        '55rem',
        collapsible: true
    }, {
        min: '20px'
    }]);

    const formik = useFormik({
        initialValues:      initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {

            if (validatePhone(values.phone)) {
                return;
            }

            // create user
            if (isCreateUser) {
                const payload = {
                    dateFormat:     values.dateFormat,
                    defaultCompany: values.defaultCompany,
                    email:          values.email,
                    firstName:      values.firstName,
                    lastName:       values.lastName,
                    phone:          values.phone,
                    timeZone:       values.timeZone ? values.timeZone.name : null,
                    timeFormat:     values.timeFormat,
                    userTypes:      [],
                    address
                };

                let userTypes = [];
                selectedUserTypeList.forEach(item => {
                    if (item.usertype === 'Technician') {
                        let tempUserTypeItem = {
                            companyId:      companyInfo.companyId,
                            usertype:       item.usertype,
                            userAttributes: {
                                title:           '',
                                startingAddress: item.userAttributes.startingAddress,
                                skillLevel:      item.userAttributes.skillLevel,
                                skillTypes:      [item.userAttributes.skillTypes]
                            }
                        };
                        userTypes.push(tempUserTypeItem);
                    } else {
                        let tempUserTypeItem = {
                            companyId: companyInfo.companyId,
                            usertype:  item.usertype
                        };
                        userTypes.push(tempUserTypeItem);
                    }
                });

                // on create of a user, require at least one user type to be selected
                if (!isEmpty(userTypes)) {
                    payload.userTypes = userTypes;
                    await createUser(payload);
                }
            } else {
                const payload = {
                    ...values,
                    defaultCompany: values.defaultCompany,
                    timeZone:       values.timeZone ? values.timeZone.name : null,
                    address
                };

                // update user details API needs defaultCompanyId instead of defaultCompany
                delete payload.defaultCompany;

                // onChange of userDetails update otherwise ignore
                if (formik.dirty) {
                    await updateUser(selectedUser.id, payload, {isSuperUser: values.isSuperUser});
                }

                // check weather userType has unsaved value
                const unsavedUserType = selectedUserTypeList.filter(item => !item.id);
                
                if (!isEmpty(unsavedUserType)) {
                    let unsavedUserTypeObj = {...unsavedUserType[0]};
                    if (unsavedUserTypeObj.usertype === 'Technician') {
                        // remove isSameAddress field
                        if ('isSameAddress' in unsavedUserTypeObj.userAttributes) {
                            delete unsavedUserTypeObj.userAttributes['isSameAddress'];
                        }
                        unsavedUserTypeObj.userAttributes.title = '';
                        unsavedUserTypeObj.userAttributes.skillTypes = [unsavedUserTypeObj.userAttributes.skillTypes];
                    }

                    let id = await addUserType(selectedUser.id, companyInfo.companyId, unsavedUserTypeObj);
                    selectedUserTypeList.forEach(item => {
                        if (item.usertype === unsavedUserTypeObj.usertype) {
                            item.id = id;
                        }
                    });
                }

                if (!isEmpty(userApplications)) {
                    let applicationIds = [];
                    userApplications.forEach(item => {
                        if (item.enabled) {
                            applicationIds.push(item.applicationId);
                        }
                    });

                    // add user applications 
                    if (createUserApplication) {
                        const applicationPayload =    {
                            userId:       selectedUser.id,
                            companyId:    companyInfo.companyId,
                            applications: {
                                ids: [...applicationIds]
                            }
                        };
    
                        await createUserCompanyApplication(applicationPayload);
                        handleFetchUserApplications(selectedUser.id, companyInfo.companyId);
                        setCreateUserApplication(false);
                    // update user company applicationos
                    } else {
                        // check weather user Applications has unsaved value
                        if (!isEqual(userApplications, userApplicationsCopy)) {
                            // copy of userApplication for updateUserCompanyApplication API validation
                            setUserApplicationsCopy(cloneDeep(userApplications));

                            const applicationPayload =    {
                                applications: {
                                    ids: [...applicationIds]
                                }
                            };
        
                            await updateUserCompanyApplication(userApplicationId, applicationPayload);
                            handleFetchUserApplications(selectedUser.id, companyInfo.companyId);
                        }
                    }
                }
            }
        }
    });

    useEffect(() => {
        if (!isEmpty(applications)) {
            setUserApplications(applications);
        }
    }, [applications]);

    useEffect(() => {
        const init = async () => {
            const fullTimeZone = selectedUser.timeZone ? TimezoneOffsets.find(tz => tz.name === selectedUser.timeZone) : null;
            let address = '';
            let defaultCompanyId = selectedUser.defaultCompanyId;

            if (selectedUser.address && selectedUser.address.formattedAddress) {
                address = selectedUser.address && selectedUser.address.formattedAddress;
            }

            if (defaultCompanyId && defaultCompanyId === EMPTY_GUID) {
                defaultCompanyId = '';
            }

            let userData = {
                ...selectedUser,
                timeZone:       fullTimeZone,
                addressSearch:  address,
                defaultCompany: defaultCompanyId,
                phone:          selectedUser.phone ? selectedUser.phone : '',
                dateFormat:     selectedUser.dateFormat ? selectedUser.dateFormat : '',
                timeFormat:     selectedUser.timeFormat ? selectedUser.timeFormat : '',
            };

            setInitialValues(userData);
            setViewSuperUserCheckbox(currentUser.isSuperUser && (selectedUser.cognitoId && selectedUser.cognitoId !== EMPTY_GUID) && (selectedUser.email.includes('@key2act.com') || selectedUser.email.includes('@wennsoft.com')));
            // unique userTypes
            let uniqueUserTypes = [
                ...new Map(selectedUser.userTypes.map((item) => [item['usertype'], item])).values(),
            ];
            setSelectedUserTypeList(uniqueUserTypes);
            handleGeocodeAddress(address);

            // client/sites that are associated with this user
            const userContacts = await fetchUserContactDetails(selectedUser.id, companyInfo.companyId);
            setUserContactDetails(userContacts);

            // fetch all enabled user applications 
            handleFetchUserApplications(selectedUser.id, companyInfo.companyId);
        };

        if ( selectedUser && selectedUser.id ) init();
    }, [selectedUser]);

    const handleFetchUserApplications = async (selectedUser, companyId) => {
        // fetch all enabled user applications 
        const applicationResponse = await fetchUserApplications(selectedUser, companyId);
        let applicationIds = [];
        
        if (!isEmpty(applicationResponse) && !isEmpty(applicationResponse.applications) && applicationResponse.applications.ids) {
            applicationIds = applicationResponse.applications.ids;
            setCreateUserApplication(false);
        }
        
        const tempApplications = [...applications];

        tempApplications.forEach(item => {
            if (isEmpty(applicationIds)) {
                item.enabled = false;
            } else {
                if (applicationIds.includes(item.applicationId)) {
                    item.enabled = true;
                } else {
                    item.enabled = false;
                }
            }
        });

        setUserApplications(tempApplications);
        // copy of userApplication for updateUserCompanyApplication API validation
        setUserApplicationsCopy(cloneDeep(tempApplications));
        setUserApplicationId(applicationResponse.id);
        // fetch all scopes for permission tab.
        await fetchAllScopes();
    };

    const handleGeocodeAddress = async address => {
        try {
            const results = await geocodeByAddress(address);
            const latLng = await getLatLng(results[0]);

            const streetNumber = results[0].address_components.find(result => {
                return result.types.includes('street_number');
            });

            const route = results[0].address_components.find(result => {
                return result.types.includes('route');
            });

            const city = results[0].address_components.find(result => {
                return result.types.includes('locality');
            });

            const administrativeAreaLevel2 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_2');
            });

            const administrativeAreaLevel3 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_3');
            });

            const state = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_1');
            });

            const zip = results[0].address_components.find(result => {
                return result.types.includes('postal_code');
            });

            const country = results[0].address_components.find(result => {
                return result.types.includes('country');
            });

            const address1Check = streetNumber ? `${streetNumber.long_name} ${route.long_name}` : route.long_name;
            const cityCheck = city ? city.long_name : administrativeAreaLevel3.long_name;

            formik.setFieldValue('addressSearch', results[0].formatted_address);

            setAddress1(address1Check);
            setCity(cityCheck);
            setState(state.short_name);
            setZip(zip.long_name);

            setAddress({
                latitude:                 get(latLng, 'lat', ''),
                longitude:                get(latLng, 'lng', ''),
                administrativeAreaLevel1: get(state, 'short_name', ''),
                administrativeAreaLevel2: get(administrativeAreaLevel2, 'long_name', ''),
                country:                  get(country, 'short_name', ''),
                locality:                 cityCheck,
                postalCode:               get(zip, 'long_name', ''),
                route:                    get(route, 'long_name', ''),
                streetNumber:             streetNumber ? streetNumber.long_name : '',
                formattedAddress:         get(results[0], 'formatted_address', '')
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error: ', error);
        }
    };

    if (!isEmpty(formik.values.addressSearch) && isEmpty(address1)) {
        handleGeocodeAddress(formik.values.addressSearch);
    }
    
    const handleTimeZoneChange = (event, timezone) => {
        formik.setFieldValue('timeZone', timezone);
    };

    const onHorizontalChange = event => {
        setHorizontalPanes(event.newState);
    };

    const createOrUpdateUser = () => {
        if (isEmpty(selectedUserTypeList)) {
            setIsWarningModalOpen(true);
        } else {
            formik.handleSubmit();
        }
    };

    const handleNextClick = () => {
        setUserTabIndex(userTabIndex + 1);
    };

    const handleFormChange = (v, d, e, fv) => {
        const phoneNumber = `+${v}`;
        formik.setFieldValue('phone', phoneNumber);
    };

    const validatePhone = (phone) => {
        return phone ? !isValidPhoneNumber(phone) : true;
    };

    return (
        <div className={classes.userformColumn}>
            <div className={classes.userDetails}>
                <Splitter
                    style={{
                        height: splitterHeight
                    }} panes={horizontalPanes} onChange={onHorizontalChange}
                >
                    <div className={classes.UserInputColumn}>
                        <p className={classes.userFormTitleField}>Details</p>
                        <div className={classes.inputs}>
                            <div className={classes.userFirstRow}>
                                <img className={classes.roundBorderUserImage} src={'/assets/images/blank-profile.png'} alt="card flavor" />
                                <div className={classes.userNameFields}>
                                    <div className={classes.formRow}>
                                        <FormControl className={classes.userInputWrapper}>
                                            <TextField
                                                id="email"
                                                name="email"
                                                type="text"
                                                disabled={isCreateUser ? false : true}
                                                placeholder="Enter"
                                                size="small"
                                                variant="outlined"
                                                value={formik.values.email}
                                                label={<span>Email Address<span className={classes.required}> *</span></span>}
                                                onChange={formik.handleChange}
                                                InputLabelProps={{
                                                    classes: {
                                                        error:   classes.error,
                                                        focused: classes.labelFocused,

                                                    }
                                                }}
                                                InputProps={{
                                                    onBlur:  formik.handleBlur,
                                                    classes: {
                                                        error: classes.cssErrorUnderline,
    
                                                        root: classes.input
                                                    }
                                                }}
                                                FormHelperTextProps={{
                                                    classes: {
                                                        error: classes.error
                                                    }
                                                }}
                                                error={Boolean(formik.errors.email) && Boolean(formik.touched.email)}
                                                helperText={Boolean(formik.touched.email) && formik.errors.email}
                                            />
                                        </FormControl>
                                    </div>
                                    <div className={classes.formRow}>
                                        <FormControl className={classes.userNameWrapper}>
                                            <TextField
                                                id="firstName"
                                                name="firstName"
                                                type="text"
                                                placeholder="Enter"
                                                size="small"
                                                variant="outlined"
                                                value={formik.values.firstName}
                                                label={<span>First Name<span className={classes.required}> *</span></span>}
                                                onChange={formik.handleChange}
                                                InputLabelProps={{
                                                    classes: {
                                                        error:   classes.error,
                                                        focused: classes.labelFocused,

                                                    }
                                                }}
                                                InputProps={{
                                                    onBlur:  formik.handleBlur,
                                                    classes: {
                                                        error: classes.cssErrorUnderline,
    
                                                        root: classes.input
                                                    }
                                                }}
                                                FormHelperTextProps={{
                                                    classes: {
                                                        error: classes.error
                                                    }
                                                }}
                                                error={Boolean(formik.errors.firstName) && Boolean(formik.touched.firstName)}
                                                helperText={Boolean(formik.touched.firstName) && formik.errors.firstName}
                                            />
                                        </FormControl>
                                        <FormControl className={classes.userNameWrapper}>
                                            <TextField
                                                id="lastName"
                                                name="lastName"
                                                type="text"
                                                placeholder="Enter"
                                                size="small"
                                                variant="outlined"
                                                value={formik.values.lastName}
                                                label={<span>Last Name<span className={classes.required}> *</span></span>}
                                                onChange={formik.handleChange}
                                                InputLabelProps={{
                                                    classes: {
                                                        error:   classes.error,
                                                        focused: classes.labelFocused,

                                                    }
                                                }}
                                                InputProps={{
                                                    onBlur:  formik.handleBlur,
                                                    classes: {
                                                        error: classes.cssErrorUnderline,
    
                                                        root: classes.input
                                                    }
                                                }}
                                                FormHelperTextProps={{
                                                    classes: {
                                                        error: classes.error
                                                    }
                                                }}
                                                error={Boolean(formik.errors.lastName) && Boolean(formik.touched.lastName)}
                                                helperText={Boolean(formik.touched.lastName) && formik.errors.lastName}
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.formRow}>
                                <PlacesAutocomplete
                                    value={formik.values.addressSearch}
                                    onChange={address => formik.setFieldValue('addressSearch', address)}
                                    onSelect={(handleGeocodeAddress)}
                                >
                                    {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                                        <React.Fragment>
                                            <FormControl variant="outlined" size="small" className={classes.inputWrapper}>
                                                <TextField
                                                    {...getInputProps({
                                                        name: 'addressSearch',
                                                        id:   'addressSearch'
                                            
                                                    })}
                                                    InputProps={{
                                                        onBlur:  formik.handleBlur,
                                                        classes: {
                                                            root: classes.input
                                                        }
                                                    }}
                                                    placeholder="Enter"
                                                    variant="outlined"
                                                    size="small"
                                                    label={<span>Search for Address<span className={classes.required}> *</span></span>}
                                                    classes={{
                                                        root: classes.input
                                                    }}
                                                    error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                                />
                                                {formik.touched.addressSearch &&
                                        formik.errors.addressSearch && <FormHelperText className={classes.error}>{formik.errors.addressSearch}</FormHelperText>}
                                            </FormControl>
                                            {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                                <List>
                                                    {suggestions.map((suggestion, index) => {
                                                        return (
                                                            <div key={index}>
                                                                <ListItem
                                                                    button
                                                                    className={classes.autoCompleteListItem}
                                                                    {...getSuggestionItemProps(suggestion)}
                                                                >
                                                                    <ListItemText primary={suggestion.description} />
                                                                </ListItem>
                                                            </div>
                                                        );
                                                    })}
                                                </List>
                                                <GoogleLogo />
                                            </Paper>}
                                        </React.Fragment>
                                    )}
                                </PlacesAutocomplete>
                            </div>
                            <div className={classes.formRow}>
                                <FormControl className={classes.inputWrapper}>
                                    <TextField
                                        id="address1"
                                        name="address1"
                                        type="text"
                                        value={address1}
                                        label="Address"
                                        disabled
                                        size="small"
                                        InputLabelProps={{
                                            classes: {
                                                error:   classes.error,
                                                focused: classes.labelFocused
                                            }
                                        }}
                                        variant="outlined"
                                        InputProps={{
                                            classes: {
                                                error: classes.cssErrorUnderline
                                            }
                                        }}
                                    />
                                </FormControl>
                            </div>
                            <div className={classes.formRow}>
                                <FormControl className={`${classes.inputWrapper} ${classes.userInputWrapperPadding}`} >
                                    <TextField
                                        id="city"
                                        name="city"
                                        type="text"
                                        value={city}
                                        label="City"
                                        disabled
                                        placeholder="Enter"
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{
                                            classes: {
                                                error:   classes.error,
                                                focused: classes.labelFocused
                                            }
                                        }}
                                        InputProps={{
                                            classes: {
                                                error: classes.cssErrorUnderline
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormControl className={`${classes.inputWrapperHalf} ${classes.userInputWrapperPadding}`}>
                                    <TextField
                                        id="state"
                                        name="state"
                                        type="text"
                                        value={state}
                                        label="State"
                                        disabled
                                        placeholder="Enter"
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{
                                            classes: {
                                                error:   classes.error,
                                                focused: classes.labelFocused
                                            }
                                        }}
                                        InputProps={{
                                            classes: {
                                                error: classes.cssErrorUnderline
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormControl className={classes.inputWrapperHalf}>
                                    <TextField
                                        id="zip"
                                        name="zip"
                                        type="text"
                                        value={zip}
                                        label="Zip Code"
                                        disabled
                                        placeholder="Enter"
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{
                                            classes: {
                                                error:   classes.error,
                                                focused: classes.labelFocused
                                            }
                                        }}
                                        InputProps={{
                                            classes: {
                                                error: classes.cssErrorUnderline
                                            }
                                        }}
                                    />
                                </FormControl>
                            </div>
                            <div className={classes.formRow}>
                                <FormControl className={`${classes.inputWrapperHalf} ${classes.phoneNumberWrapper}`}>
                                    <BobContactPhoneInput
                                        name="phoneNumber"
                                        label="Phone Number"
                                        data-cy="user-phone"
                                        defaultCode="us"
                                        overrideHeight={true}
                                        value={formik.values.phone}
                                        onChange={
                                            (v, d, e, fv) => {
                                                handleFormChange(v, d, e, fv);
                                            }
                                        }
                                    />
                                    {formik.touched.phone &&
                                        formik.errors.phone && <FormHelperText className={classes.error}>{formik.errors.phone}</FormHelperText>}
                                    {formik.values.phone && validatePhone(formik.values.phone) && 
                                        <FormHelperText className={classes.error}>{'Invalid phone number'}</FormHelperText>}
                                </FormControl>
                                <FormControl className={`${classes.inputWrapperHalf} ${classes.timeZoneWrapper}`}>
                                    <AutoComplete
                                        id="combo-box-timezones"
                                        options={TimezoneOffsets}
                                        getOptionLabel={(option) => option.name || ''}
                                        value={formik.values.timeZone}
                                        getOptionSelected={(option, value) => option.name === value.name}
                                        onChange={handleTimeZoneChange}
                                        size="small"
                                        required
                                        renderInput={(params) => 
                                            <TextField 
                                                {...params} 
                                                label="Time Zone" 
                                                variant="outlined" 
                                                error={Boolean(formik.errors.timeZone) && Boolean(formik.touched.timeZone)}
                                            />}
                                    />
                                </FormControl>
                            </div>
                            <div className={classes.formRow}>
                                <FormControl variant="outlined" size="small" className={`${classes.inputWrapperHalf} ${classes.timeFormatWrapper}`}>
                                    <InputLabel
                                        htmlFor="dateFormat"
                                        classes={{
                                            error:   classes.error,
                                            focused: classes.labelFocused
                                        }}
                                        error={Boolean(formik.errors.dateFormat) && Boolean(formik.touched.dateFormat)}
                                    >
                                        Date Format<span className={classes.required}> *</span>
                                    </InputLabel>
                                    <Select
                                        value={formik.values.dateFormat}
                                        id="dateFormat"
                                        onChange={formik.handleChange}
                                        name="dateFormat"
                                        classes={{
                                            root: classes.input
                                        }}
                                        inputProps={{
                                            onBlur: formik.handleBlur
                                        }}
                                        error={Boolean(formik.errors.dateFormat) && Boolean(formik.touched.dateFormat)}
                                    >
                                        {
                                            DATE_FORMATS.map(dateFormat =>
                                                <MenuItem
                                                    key={dateFormat.id}
                                                    value={dateFormat.id}
                                                >
                                                    {dateFormat.name}
                                                </MenuItem>
                                            )
                                        }
                                    </Select>
                                    {formik.touched.dateFormat &&
                                        formik.errors.dateFormat && <FormHelperText className={classes.error}>{formik.errors.dateFormat}</FormHelperText>}
                                </FormControl>
                                <FormControl variant="outlined" size="small" className={classes.inputWrapperHalf}>
                                    <InputLabel
                                        htmlFor="timeFormat"
                                        classes={{
                                            error:   classes.error,
                                            focused: classes.labelFocused
                                        }}
                                        error={Boolean(formik.errors.timeFormat) && Boolean(formik.touched.timeFormat)}
                                    >
                                        Time Format<span className={classes.required}> *</span>
                                    </InputLabel>
                                    <Select
                                        value={formik.values.timeFormat}
                                        id="timeFormat"
                                        onChange={formik.handleChange}
                                        name="timeFormat"
                                        classes={{
                                            root: classes.input
                                        }}
                                        inputProps={{
                                            onBlur: formik.handleBlur
                                        }}
                                        error={Boolean(formik.errors.timeFormat) && Boolean(formik.touched.timeFormat)}
                                    >
                                        {
                                            TIME_FORMATS.map(timeFormat =>
                                                <MenuItem
                                                    key={timeFormat.id}
                                                    value={timeFormat.id}
                                                >
                                                    {timeFormat.name}
                                                </MenuItem>
                                            )
                                        }
                                    </Select>
                                    {formik.touched.timeFormat &&
                                        formik.errors.timeFormat && <FormHelperText className={classes.error}>{formik.errors.timeFormat}</FormHelperText>}
                                </FormControl>
                            </div>
                            {
                                ( !isCreateUser && currentUser.isSuperUser && selectedUser.cognitoId) ||
                                ( !isCreateUser && selectedUser && currentUser.id === selectedUser.id && selectedUser.cognitoId )?
                                    <div className={classes.formRow}>
                                        <FormControl variant="outlined" size="small" className={`${classes.inputWrapperHalf} ${classes.defaultCompanyWrapper}`}>
                                            <InputLabel
                                                htmlFor="defaultCompany"
                                                classes={{
                                                    error:   classes.error,
                                                    focused: classes.labelFocused,
                                                }}
                                                error={Boolean(formik.errors.defaultCompany) && Boolean(formik.touched.defaultCompany)}
                                            >
                                                Default Company
                                            </InputLabel>
                                            <Select
                                                value={formik.values.defaultCompany}
                                                id="defaultCompany"
                                                onChange={formik.handleChange}
                                                name="defaultCompany"
                                                classes={{
                                                    root: classes.input
                                                }}
                                                inputProps={{
                                                    onBlur: formik.handleBlur
                                                }}
                                                disabled={companies.findIndex(c => c.company.id === selectedUser.defaultCompanyId) === -1}
                                                error={Boolean(formik.errors.defaultCompany) && Boolean(formik.touched.defaultCompany)}
                                            >
                                                {
                                                    companies.map(item =>
                                                        <MenuItem
                                                            key={item.company.id}
                                                            value={item.company.id}
                                                        >
                                                            {item.company.name}
                                                        </MenuItem>
                                                    )
                                                }
                                            </Select>
                                            {formik.touched.defaultCompany &&
                                                formik.errors.defaultCompany && <FormHelperText className={classes.error}>{formik.errors.defaultCompany}</FormHelperText>}
                                        </FormControl>
                                        {
                                            viewSuperUserCheckbox ? 
                                                <FormControl variant="outlined" size="small" className={classes.inputWrapperHalf}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={formik.values.isSuperUser}
                                                                value="isSuperUser"
                                                                name="isSuperUser"
                                                                color="primary"
                                                                onChange={formik.handleChange}
                                                                disabled={!formik.dirty && formik.values.isSuperUser}
                                                            />
                                                        }
                                                        label={
                                                            <div style={{fontSize: '1.2rem'}}>
                                                                Super User
                                                            </div>
                                                        }
                                                    />
                                                </FormControl>
                                                : null
                                        }
                                    </div>
                                    : null
                            }
                        </div>
                    </div>
                    <div className={classes.technicianColumn}>
                        <UserTabsContainer 
                            selectedUser={selectedUser}
                            createOrUpdateUser={() => createOrUpdateUser()}
                            isCreateUser={isCreateUser}
                            selectedUserTypeList={selectedUserTypeList}
                            setSelectedUserTypeList={setSelectedUserTypeList}
                            userFormattedAddress={formik.values.addressSearch}
                            userApplications={userApplications}
                            setUserApplications={setUserApplications}
                            userContactDetails={userContactDetails}
                            userTabIndex={userTabIndex}
                            setUserTabIndex={setUserTabIndex}
                            contextState={contextState}
                            setContextState={setContextState}
                            onCancelClick={() => onCancelClick()}
                        />
                    </div>
                </Splitter>
            </div>
            {
                (isCreateUser && userTabIndex !== 2) || (!isCreateUser && userTabIndex !== 3) ?
                    <div className={classes.userButtons}>
                        <Button
                            className={classes.Button}
                            variant="contained"
                            onClick={() => onCancelClick()}
                        >
                            Cancel
                        </Button>
                        {
                            (isCreateUser && userTabIndex === 1) || (!isCreateUser && userTabIndex === 2) ?
                                <BobConfirmButton
                                    className={classes.ButtonWithMargin}
                                    label={'Next'}
                                    onClick={() => handleNextClick()}
                                />
                                : 
                                <BobConfirmButton
                                    className={classes.ButtonWithMargin}
                                    label={isCreateUser ? 'Apply' : 'Update'}
                                    onClick={() => createOrUpdateUser()}
                                />
                        }
                    </div>
                    : null
            }
            {
                isnWarningModalOpen ?
                    <BobModal
                        title={'User Type'}
                        applyHidden={true}
                        hidebuttons={true}
                        isRemoveFooter={true}
                        onClose={() => setIsWarningModalOpen(false)}
                    >
                        <React.Fragment>
                            <ErrorOutlineIcon className={classes.warningIcon} />
                            <div className={classes.text}>
                                <p className={classes.entityName}>User Type is required to create a user</p>
                            </div>
                            <Button
                                className={classes.Button}
                                onClick={() => setIsWarningModalOpen(false)}
                                variant="contained"
                            >
                                Cancel
                            </Button>
                        </React.Fragment>
                    </BobModal> : ''
            }
            {
                bobLoading.includes('CreateUser') || bobLoading.includes('UpdateUser') || bobLoading.includes('DeleteUserType') ||
                bobLoading.includes('AddUserType') || bobLoading.includes('CreateUserCompanyApplication') ||
                bobLoading.includes('UpdateUserCompanyApplication') || bobLoading.includes('FetchUserContactDetails') ||
                bobLoading.includes('fetchUserContextData') || bobLoading.includes('fetchRoleScopes') ||
                bobLoading.includes('updateUserContextWithScopes') || bobLoading.includes('deleteExistingUserScopes') ?
                    <BobLoadingMask
                        className={classes.Loader}
                    />
                    :
                    null
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        applications: state.companyInfo.applications,
        bobLoading:   state.bob.bobLoading,
        companies:    state.core.companies,
        companyInfo:  state.companyInfo,
        currentUser:  state.currentUser
    };
};

const mapDispatchToProps = {
    ...BobActions,
    ...DashboardActions,
    ...RoleActions,
    ...ServiceManagementActions,
    ...SvcRequestActions,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserDetails);
